import React, { useState } from "react";
import { BookCover } from "@/components/BookCover";
import { PageHead } from "@/components/PageHead";
import { Seo } from "@/components/Seo";
import type { LanguageBinding } from "@/hooks/useLanguage";
import { cx } from "@/lib/classnames";

const WAITLIST_ENDPOINT = "https://formsubmit.co/ajax/b698aee8d9f1226f990db82a31024e0d";

// Optional Google Apps Script URL — mirrors each signup into a private
// Google Sheet for sortable / exportable archive. Safe to commit: the
// endpoint only write-appends to the owner's sheet; no read capability.
const SHEETS_ENDPOINT =
  "https://script.google.com/macros/s/AKfycbwGOHFEBIioz1qs1UvSlc1pvZtBv1GU9GP9AS8YhgdRrX-dho3XLWQnr1uI0uK_RFDU/exec";

export const English: React.FC<{ language: LanguageBinding }> = ({ language }) => {
  const { t, lang, isRTL } = language;
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [flipped, setFlipped] = useState(false);

  // Back-of-cover panel for the English edition. The English edition
  // hasn't been printed yet so there's no physical back to photograph;
  // we render a translated version of the Arabic back-cover bio inside
  // a dark panel matching the Horus poster's mood, giving English-side
  // visitors the same "what's behind the cover?" depth as the Arabic
  // page's flippable back image.
  const englishBackOverlay = (
    <div
      className="absolute inset-0 px-4 py-5 overflow-auto"
      style={{
        background:
          "linear-gradient(180deg, #14110e 0%, #0b0908 100%)",
        color: "var(--parchment)",
      }}
    >
      <div
        className="ff-en-sc tracked-widest text-[9px] mb-2"
        style={{ color: "var(--vermillion-2)" }}
      >
        ABOUT THE AUTHOR
      </div>
      <div
        className="ff-en-body text-[10px] leading-snug space-y-2"
        style={{ color: "var(--parchment)" }}
      >
        {t.aboutPage.bioFromBackCover.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>
    </div>
  );

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status === "sending") return;
    if (!email.includes("@")) return;
    setStatus("sending");
    const payload = {
      _subject: "English edition waitlist signup",
      _template: "table",
      _captcha: "false",
      form_type: "waitlist-english-edition",
      language: lang,
      email,
    };
    try {
      const res = await fetch(WAITLIST_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // Mirror to the sheet archive in parallel (non-blocking). text/plain
      // avoids a CORS preflight Apps Script doesn't answer; the script
      // parses the body as JSON regardless.
      if (SHEETS_ENDPOINT) {
        void fetch(SHEETS_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "text/plain;charset=utf-8" },
          body: JSON.stringify(payload),
        }).catch(() => {});
      }
      setStatus("sent");
    } catch {
      setStatus("error");
    }
  }
  const sent = status === "sent";
  return (
    <>
      <Seo
        title={t.meta.english.title}
        description={t.meta.english.description}
        lang={lang}
        path="/english"
      />
      <PageHead
        kicker={t.englishPage.kicker}
        title={t.englishPage.title}
        tagline={t.englishPage.subtitle}
        isRTL={isRTL}
      />

      <section className="relative" style={{ background: "var(--ink-2)" }}>
        <div className="grain" />
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-20 md:py-28 relative">
          <div className="grid md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-5 flex justify-center">
              <div className="relative">
                <div
                  className="absolute -inset-6 rounded-full"
                  style={{
                    background:
                      "radial-gradient(ellipse at center, rgba(200,68,30,.1), transparent 70%)",
                  }}
                />
                <div className="frame-gold">
                  <BookCover
                    lang="en"
                    size="md"
                    flippable
                    flipped={flipped}
                    onFlip={() => setFlipped((f) => !f)}
                    backOverlay={englishBackOverlay}
                    flipLabel={flipped ? t.bookPage.flipToFront : t.bookPage.flipToBack}
                  />
                </div>
                {/* Badge floats well to the right of the cover so it
                    doesn't share horizontal space with the title text. On
                    desktop it sits ~3cm past the cover's right edge; on
                    mobile a smaller offset keeps it from sliding off the
                    viewport. Uses right- (not end-) so RTL UI doesn't
                    mirror it onto the text-heavy left side of the cover. */}
                <div
                  className="absolute -top-5 -right-5 md:-right-[45px] px-3 py-1 rounded-full whitespace-nowrap"
                  style={{
                    background: "var(--vermillion)",
                    color: "#fff3df",
                    boxShadow: "0 6px 18px rgba(0,0,0,.45)",
                  }}
                >
                  <span
                    className={cx(
                      isRTL
                        ? "ff-ar-sans text-[12px] leading-none"
                        : "ff-en-sc tracked-widest text-[9px]",
                    )}
                  >
                    {isRTL ? "قريبًا" : "COMING SOON"}
                  </span>
                </div>
              </div>
            </div>
            <div className="md:col-span-7" style={{ color: "var(--parchment)" }}>
              <p
                className={cx(isRTL ? "ff-ar-sans" : "ff-en-body", "text-lg leading-relaxed")}
                style={{ color: "var(--muted-dark)" }}
              >
                {t.englishPage.body1}
              </p>
              <p
                className={cx(isRTL ? "ff-ar-sans" : "ff-en-body", "mt-5 text-lg leading-relaxed")}
                style={{ color: "var(--muted-dark)" }}
              >
                {t.englishPage.body2}
              </p>

              <div
                className="mt-10 p-6 rounded-sm"
                style={{
                  border: "1px solid var(--hairline-dark)",
                  background: "var(--card-surface)",
                }}
              >
                <div
                  className={cx(
                    isRTL ? "ff-ar-sans" : "ff-en-sc tracked-widest",
                    "text-[11px] mb-4",
                  )}
                  style={{ color: "var(--vermillion-2)" }}
                >
                  {t.englishPage.formLabel}
                </div>
                {sent ? (
                  <div
                    className={cx(isRTL ? "ff-ar-serif" : "ff-en-serif italic", "text-xl")}
                    style={{ color: "var(--gold-2)" }}
                  >
                    {t.englishPage.formThanks}
                  </div>
                ) : (
                  <>
                    <form
                      onSubmit={onSubmit}
                      className="flex flex-col sm:flex-row gap-3"
                      noValidate
                    >
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t.englishPage.formPlaceholder}
                        required
                        className={cx(
                          "flex-1 bg-transparent px-4 py-3 rounded-full outline-none text-sm",
                          isRTL ? "ff-ar-sans" : "ff-en-body",
                        )}
                        style={{
                          color: "var(--parchment)",
                          border: "1px solid var(--hairline-dark)",
                        }}
                      />
                      <button
                        type="submit"
                        disabled={status === "sending"}
                        className={cx(
                          isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide",
                          "text-[13px] md:text-[14px] px-6 py-3 rounded-full transition-opacity",
                          status === "sending" && "opacity-60 cursor-not-allowed",
                        )}
                        style={{ background: "var(--vermillion)", color: "#fff3df" }}
                      >
                        {t.englishPage.formCta}
                      </button>
                    </form>
                    {status === "error" && (
                      <div
                        className={cx(isRTL ? "ff-ar-sans" : "ff-en-body", "mt-3 text-xs")}
                        style={{ color: "var(--vermillion-2)" }}
                      >
                        {isRTL
                          ? "تعذّر إرسال بريدك الإلكتروني. حاول مرة أخرى لاحقًا."
                          : "Could not submit — please try again later."}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
